// Per-entry & all-entries exports (CSV + branded landscape PDF) for the Vehicle
// Asset Log Book (Trailer & Equipment attach/detach sessions). Reuses the exact
// same scaffolding as the Vehicle Driver Log Book so both stay visually consistent.
import { formatDuration, getFileTimestamp } from './timezone';
import {
  newLandscapeDoc,
  addBrandHeader,
  addFooters,
  loadLogoPng,
  downloadBlob,
  rowsToCsv,
  safeName,
  fmtCoords,
  tripLocMapsUrl,
  csvMapsLink,
  SLATE_900,
  SLATE_600,
  SLATE_500,
  GREEN,
  BORDER,
  formatExportDateTime,
} from './tripExport';

const stamp = () => getFileTimestamp();

const HEAD_BG = [241, 245, 249];
const ROW_BG = [248, 250, 252];

export const assetTypeLabel = (e) => {
  const k = String(e.role || e.person_type || '').toLowerCase();
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : 'Asset';
};

// ----------------------------- CSV -----------------------------

const entryCsvRows = (e, idx) => {
  const rows = [];
  rows.push([`Attachment #${idx + 1}`, e.is_active ? 'Attached now' : 'Detached']);
  rows.push(['Type', assetTypeLabel(e)]);
  rows.push(['Asset', e.entity_display_name || 'Unknown asset']);
  rows.push(['Source', e.source_label || '-']);
  rows.push(['Beacon / Credential', e.credential_identifier || '-']);
  rows.push(['Attached', formatExportDateTime(e.started_at)]);
  rows.push(['Detached', e.is_active ? 'Still attached' : formatExportDateTime(e.ended_at)]);
  rows.push(['Duration', e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-']);
  rows.push(['Attach location', csvMapsLink(e.location)]);
  rows.push(['Detach location', csvMapsLink(e.end_location)]);
  return rows;
};

export const exportAssetEntryCsv = (entry, vehicleName, idx = 0) => {
  const rows = [['Vehicle', vehicleName || ''], []].concat(entryCsvRows(entry, idx));
  downloadBlob(rowsToCsv(rows), `${safeName(vehicleName)}-asset-logbook-entry-${idx + 1}-${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

export const exportAllAssetsCsv = (entries, vehicleName, utilisation = []) => {
  let rows = [['Vehicle', vehicleName || ''], ['Attachments', String((entries || []).length)], ['Exported', formatExportDateTime(new Date().toISOString())], []];
  if ((utilisation || []).length) {
    rows.push(['Utilisation — attached time & trips per asset']);
    rows.push(['Asset', 'Type', 'Trips', 'Attached time', 'Attached now']);
    (utilisation || []).forEach((u) => rows.push([
      u.asset_name || 'Unknown asset',
      u.asset_type || '-',
      String(u.trips ?? '-'),
      u.total_attached_seconds != null ? formatDuration(u.total_attached_seconds) : '-',
      u.active ? 'Yes' : 'No',
    ]));
    rows.push([]);
  }
  (entries || []).forEach((e, idx) => { rows = rows.concat(entryCsvRows(e, idx), [[], []]); });
  downloadBlob(rowsToCsv(rows), `${safeName(vehicleName)}-asset-logbook_${stamp()}.csv`, 'text/csv;charset=utf-8;');
};

// ----------------------------- PDF -----------------------------

const COLS = [
  { label: 'Type', w: 60 },
  { label: 'Asset', w: 150 },
  { label: 'Source', w: 90 },
  { label: 'Beacon / Credential', w: 160 },
  { label: 'Attached -> Detached', w: null }, // fills remaining minus Duration
  { label: 'Duration', w: 70 },
];

const drawAssetsTable = (doc, layout, entries, startY) => {
  const { M, CONTENT_W, PAGE_H } = layout;
  const fixed = COLS.reduce((acc, c) => acc + (c.w || 0), 0);
  const cols = COLS.map((c) => ({ ...c, w: c.w == null ? Math.max(120, CONTENT_W - fixed) : c.w }));
  const xs = [];
  let cx = M;
  cols.forEach((c) => { xs.push(cx); cx += c.w; });
  const ROW_H = 16;
  let y = startY;

  const drawHeader = () => {
    doc.setFillColor(...HEAD_BG);
    doc.rect(M, y, CONTENT_W, ROW_H, 'F');
    doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_600);
    cols.forEach((c, i) => doc.text(c.label, xs[i] + 4, y + 11));
    y += ROW_H;
  };

  drawHeader();
  doc.setFont(undefined, 'normal'); doc.setFontSize(8);
  if ((entries || []).length === 0) {
    doc.setTextColor(...SLATE_500);
    doc.text('No trailer or equipment attachments found for the selected filters.', M + 4, y + 11);
    return y + ROW_H;
  }
  (entries || []).forEach((e, i) => {
    if (y + ROW_H > PAGE_H - 44) { doc.addPage(); y = 44; drawHeader(); doc.setFont(undefined, 'normal'); doc.setFontSize(8); }
    if (i % 2 === 1) { doc.setFillColor(...ROW_BG); doc.rect(M, y, CONTENT_W, ROW_H, 'F'); }
    const cells = [
      assetTypeLabel(e),
      String(e.entity_display_name || 'Unknown asset'),
      String(e.source_label || '-'),
      String(e.credential_identifier || '-'),
      `${formatExportDateTime(e.started_at)} -> ${e.is_active ? 'attached' : formatExportDateTime(e.ended_at)}`,
      e.duration_seconds != null ? formatDuration(e.duration_seconds) : '-',
    ];
    cells.forEach((val, ci) => {
      doc.setTextColor(...(ci === 4 && e.is_active ? GREEN : SLATE_900));
      const fit = doc.splitTextToSize(val, cols[ci].w - 8)[0] || '';
      doc.text(fit, xs[ci] + 4, y + 11);
    });
    y += ROW_H;
  });
  return y;
};

const drawUtilisation = (doc, layout, utilisation, startY) => {
  const { M, PAGE_H, RIGHT } = layout;
  if (!(utilisation || []).length) return startY;
  let y = startY + 6;
  if (y + 24 > PAGE_H - 44) { doc.addPage(); y = 44; }
  doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.setTextColor(...SLATE_900);
  doc.text('Utilisation — attached time & trips per asset', M, y); y += 14;
  doc.setFontSize(8); doc.setFont(undefined, 'normal');
  (utilisation || []).forEach((u) => {
    if (y + 13 > PAGE_H - 44) { doc.addPage(); y = 44; }
    doc.setTextColor(...SLATE_900);
    doc.text(`${u.asset_name || 'Unknown asset'} (${u.asset_type || '-'})${u.active ? ' — attached now' : ''}`, M, y);
    const right = `${u.trips ?? 0} ${u.trips === 1 ? 'trip' : 'trips'}  |  ${u.total_attached_seconds != null ? formatDuration(u.total_attached_seconds) : '-'} attached`;
    doc.setTextColor(...SLATE_600);
    doc.text(right, RIGHT, y, { align: 'right' });
    y += 13;
  });
  return y;
};

export const exportAssetEntryPdf = async (entry, vehicleName, idx = 0) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Vehicle Asset Attachment', vehicleName);
  y = drawAssetsTable(doc, layout, [entry], y);
  // Location links for the single entry
  const startUrl = tripLocMapsUrl(entry.location);
  const endUrl = tripLocMapsUrl(entry.end_location);
  if (startUrl || endUrl) {
    y += 14;
    doc.setFontSize(8); doc.setFont(undefined, 'normal'); doc.setTextColor(37, 99, 235);
    let lx = layout.M;
    if (startUrl) { const t = `Attach: ${fmtCoords(entry.location)}`; doc.textWithLink(t, lx, y, { url: startUrl }); lx += doc.getTextWidth(t) + 24; }
    if (endUrl) { const t = `Detach: ${fmtCoords(entry.end_location)}`; doc.textWithLink(t, lx, y, { url: endUrl }); }
  }
  addFooters(doc, layout);
  doc.save(`${safeName(vehicleName)}-asset-logbook-entry-${idx + 1}-${stamp()}.pdf`);
};

export const buildAllAssetsPdf = async (entries, vehicleName, utilisation = []) => {
  const { doc, layout } = newLandscapeDoc();
  const logo = await loadLogoPng();
  let y = addBrandHeader(doc, layout, logo, 'Vehicle Asset Log Book', `${vehicleName || '-'} \u00b7 ${(entries || []).length} attachments`);
  y = drawAssetsTable(doc, layout, entries, y);
  y = drawUtilisation(doc, layout, utilisation, y);
  addFooters(doc, layout);
  return { doc, filename: `${safeName(vehicleName)}-asset-logbook_${stamp()}.pdf` };
};

export const exportAllAssetsPdf = async (entries, vehicleName, utilisation = []) => {
  const { doc, filename } = await buildAllAssetsPdf(entries, vehicleName, utilisation);
  doc.save(filename);
};
